import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Check, Sparkles, Award, Gift, Users } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const tierIcons = {
  entry_anchor: Sparkles,
  middle_anchor: Award,
  premium_anchor: Gift,
  decoy_effect: Award,
  premium_bias: Gift,
  top_anchor: Users
};

export default function RadianceProduct() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchRadianceProducts();
  }, []);

  const fetchRadianceProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products`, {
        params: { category: 'Skin' }
      });
      
      // Filter and sort Radiance products by price
      const radianceProducts = response.data
        .filter(p => p.name.includes('Radiance'))
        .sort((a, b) => a.price - b.price);
      
      setProducts(radianceProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen section-padding bg-soft-ivory flex items-center justify-center">
        <p className="text-dark-cedar/60">Loading Tiger Radiance collection...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-soft-ivory" data-testid="radiance-product-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[70vh] flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(247, 241, 232, 0.8), rgba(226, 199, 164, 0.9)), url('https://images.unsplash.com/photo-1762328868620-76572366d607')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto px-6 text-center space-y-6"
        >
          <p className="overline text-tiger-orange">DOCTOR-CURATED SKIN RITUAL</p>
          <h1 className="heading-hero text-dark-cedar">Tiger Radiance</h1>
          <p className="body-text text-dark-cedar/80 max-w-2xl mx-auto">
            A clinically curated tea ritual for luminous skin from within. Hibiscus, rose, and collagen-supporting herbs blend with Eastern medicine wisdom for transformative radiance.
          </p>
        </motion.div>
      </section>

      {/* Product Options - Psychology-Optimized Grid */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16 space-y-4"
          >
            <h2 className="heading-2">Choose Your Radiance Ritual</h2>
            <p className="body-text text-dark-cedar/70">
              Psychology-optimized pricing designed for your wellness journey
            </p>
          </motion.div>

          {/* Teabag Options */}
          <div className="mb-16">
            <h3 className="font-playfair text-2xl font-semibold text-dark-cedar mb-8 text-center">
              Teabag Ritual Sets
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {products.filter(p => p.product_type === 'teabag').map((product, index) => {
                const Icon = tierIcons[product.pricing_tier] || Sparkles;
                const isPopular = product.badge === 'Most Popular';
                
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`relative card-flat p-8 ${isPopular ? 'ring-2 ring-tiger-orange' : ''}`}
                  >
                    {isPopular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-tiger-orange text-soft-ivory px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                        Most Popular
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-6">
                      <Icon className="w-10 h-10 text-tiger-orange" />
                      <div className="text-right">
                        <div className="text-3xl font-bold text-dark-cedar">${product.price}</div>
                        <div className="text-sm text-dark-cedar/60">USD</div>
                      </div>
                    </div>

                    <h3 className="font-playfair text-xl font-semibold text-dark-cedar mb-2">
                      {product.quantity}
                    </h3>
                    <p className="text-dark-cedar/70 text-sm mb-6 leading-relaxed">
                      {product.description}
                    </p>

                    <div className="space-y-3 mb-6">
                      {product.benefits.slice(0, 3).map((benefit, i) => (
                        <div key={i} className="flex items-start space-x-2">
                          <Check className="w-4 h-4 text-tiger-orange mt-1 flex-shrink-0" />
                          <span className="text-sm text-dark-cedar/80">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    <Button
                      onClick={() => handleAddToCart(product)}
                      className={`w-full ${isPopular ? 'btn-primary' : 'btn-secondary'}`}
                    >
                      Add to Cart
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Premium Loose-Leaf Options */}
          <div>
            <h3 className="font-playfair text-2xl font-semibold text-dark-cedar mb-4 text-center">
              Premium Loose-Leaf Ritual Series
            </h3>
            <p className="text-center text-dark-cedar/70 mb-12 max-w-2xl mx-auto">
              Handcrafted wooden boxes for the ultimate sensory ritual experience
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.filter(p => p.product_type !== 'teabag').map((product, index) => {
                const Icon = tierIcons[product.pricing_tier] || Gift;
                const isBestValue = product.badge === 'Best Value';
                const isLuxury = product.badge === 'Luxury Edition';
                
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15 }}
                    className={`relative card-neumorphic p-8 ${isBestValue ? 'lg:scale-105' : ''}`}
                  >
                    {product.badge && (
                      <div className={`absolute -top-3 right-4 ${
                        isBestValue ? 'bg-warm-brick' : 
                        isLuxury ? 'bg-tiger-orange' : 
                        'bg-sage-green'
                      } text-soft-ivory px-4 py-1 rounded-full text-xs font-semibold`}>
                        {product.badge}
                      </div>
                    )}
                    
                    <div className="mb-6">
                      <Icon className="w-12 h-12 text-tiger-orange mb-4" />
                      <div className="flex items-baseline space-x-2">
                        <span className="text-4xl font-bold text-dark-cedar">${product.price}</span>
                        <span className="text-sm text-dark-cedar/60">USD</span>
                      </div>
                    </div>

                    <h3 className="font-playfair text-lg font-semibold text-dark-cedar mb-2 min-h-[3.5rem]">
                      {product.name.split('–')[0].trim()}
                    </h3>
                    <p className="text-dark-cedar/70 text-sm mb-4">
                      {product.packaging}
                    </p>
                    
                    <div className="bg-soft-ivory/50 rounded-lg p-4 mb-6">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-dark-cedar/70">Total:</span>
                          <span className="font-semibold text-dark-cedar">{product.quantity}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-dark-cedar/70">Servings:</span>
                          <span className="font-semibold text-dark-cedar">{product.servings}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-6">
                      {product.benefits.slice(0, 4).map((benefit, i) => (
                        <div key={i} className="flex items-start space-x-2">
                          <Check className="w-4 h-4 text-tiger-orange mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-dark-cedar/80">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    <Button
                      onClick={() => handleAddToCart(product)}
                      className={`w-full ${
                        isBestValue ? 'btn-primary' : 
                        isLuxury ? 'bg-tiger-orange hover:bg-tiger-orange/90 text-soft-ivory' : 
                        'btn-secondary'
                      }`}
                    >
                      {isLuxury ? 'Select Luxury Edition' : 'Add to Cart'}
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Value Proposition Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 bg-desert-sand rounded-2xl p-12 text-center"
          >
            <h3 className="font-playfair text-3xl font-semibold text-dark-cedar mb-4">
              Why Tiger Radiance Works
            </h3>
            <p className="body-text text-dark-cedar/80 max-w-3xl mx-auto mb-8">
              Each blend is clinically curated by Dr. Soomin with ingredients proven to support skin health from within. The ritual matters as much as the tea—consistency transforms.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div>
                <div className="text-4xl font-bold text-tiger-orange mb-2">7-14</div>
                <div className="text-sm text-dark-cedar/70">Days to visible glow</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-tiger-orange mb-2">100%</div>
                <div className="text-sm text-dark-cedar/70">Natural ingredients</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-tiger-orange mb-2">0</div>
                <div className="text-sm text-dark-cedar/70">Caffeine · gentle ritual</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
